import EditorContent from "../common/EditorContent";
import { useLineItemRequests } from "../../hooks/useLineItemRequests";
import type { PurchaseOrderResponse } from "../../api_utils/types";
import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  LegacyCard,
  Page,
  Badge,
  TextField,
  Label,
  Button,
  Box,
  IndexTable,
  PageActions,
  FormLayout,
  Layout,
  InlineStack,
} from "@shopify/polaris";
import { ImportIcon, ExportIcon, EmailFollowUpIcon, UndoIcon } from "@shopify/polaris-icons";
import EmailPdfDialog from "./support/EmailPdfDialog";
import DeleteConfirmationDialog from "../common/DeleteConfirmationDialog";
import CostAdjustments from "./support/CostAdjustments";
import { useEditableCostAdjustments } from "./support/useEditableCostAdjustments";
import Payment from "./support/Payment";
import PaymentStatusBadge from "./support/PaymentStatusBadge";
import { paymentStatuses } from "./support/paymentStatuses";
import ConfirmationDialog from "../common/ConfirmationDialog";
import CustomDatePicker from "../common/CustomDatePicker/CustomDatePicker";
import {
  deletePurchaseOrder,
  updatePurchaseOrder,
  getPurchaseOrderHorseVariants,
  importPurchaseOrderLineItems,
  emailPdf,
  downloadFile,
  useUser,
} from "../../api_utils/requests";
import { getTotal } from "./support/utils";
import {
  addGlowAnimation,
  cloneNestedObjects,
  commaDelimiter,
  datePlaceholder,
  extractMessageFromError,
  formatMoney,
  roundToTwoDecimals,
  makeLineItemsMap,
  vendorPath,
} from "../../helper_functions/utils";
import { ImportFile } from "../common/ImportFile";
import { ErrorBanner } from "../common/ErrorBanner";
import { Footer } from "../common/Footer";
import type { PurchaseOrderLineItemUpdatePayload, PurchaseOrderPaymentAttributes } from "../../api_utils/types";
import {
  PurchaseOrderState,
  type IPurchaseOrder,
  type IPurchaseOrderLineItem,
  type PurchaseOrderLineItemsQueryParams,
} from "../../api_utils/types";
import { PURCHASE_ORDER_HELP_PAGE_URL } from "../../constants";
import Scanner from "../common/Scanner";
import TableControls from "../common/TableControls";
import { useVisibleColumns } from "../../hooks/useVisibleColumns";
import { orderedColumns } from "./support/optionalColumns";
import SaveBar from "../common/SaveBar";
import { useNavigate } from "react-router-dom";
import Comments from "./Comments";
import PurchaseOrderOrderedRow from "./ordered/PurchaseOrderOrderedRow";
import HorseLink from "../common/HorseLink";
import {
  defaultOrderedClosedPurchaseOrderSortValue,
  purchaseOrderTableSortOptions,
} from "./support/orderedClosedPurchaseOrderSortOptions";

const getPurchaseOrderLineItemsPayload = (
  purchaseOrderLineItems: PurchaseOrderLineItems,
): PurchaseOrderLineItemUpdatePayload[] =>
  Object.values(purchaseOrderLineItems).map(
    (purchaseOrderLineItem: SortablePurchaseOrderLineItem): PurchaseOrderLineItemUpdatePayload => {
      return {
        id: purchaseOrderLineItem.id,
        quantity: purchaseOrderLineItem.quantity || 0,
        received: purchaseOrderLineItem.received || 0,
        cost: purchaseOrderLineItem.cost,
      };
    },
  );

type SortablePurchaseOrderLineItem = IPurchaseOrderLineItem & {
  sortIndex: number;
};

type PurchaseOrderLineItems = Record<number, SortablePurchaseOrderLineItem>;

function Index({
  response: initialResponse,
  onSave,
}: {
  readonly response: PurchaseOrderResponse;
  readonly onSave: (response: PurchaseOrderResponse) => void;
}): React.JSX.Element {
  const initialPurchaseOrder = initialResponse.purchaseOrder;
  const today = useMemo(() => datePlaceholder(new Date()), []);

  const initialPurchaseOrderWithArrivalDate = {
    ...initialPurchaseOrder,
    arrival_date: initialPurchaseOrder.arrival_date ? new Date(initialPurchaseOrder.arrival_date) : new Date(),
  };
  const [purchaseOrder, setPurchaseOrder] = useState<IPurchaseOrder>(initialPurchaseOrderWithArrivalDate);
  const originalPurchaseOrder = useRef<IPurchaseOrder>(initialPurchaseOrderWithArrivalDate);
  const [importCSVModalIsOpen, setImportCSVModalIsOpen] = useState(false);
  const [polis, setPolis] = useState<PurchaseOrderLineItems>(
    () => makeLineItemsMap<SortablePurchaseOrderLineItem>(initialResponse.line_items as SortablePurchaseOrderLineItem[]),
  );
  const [isDirty, setIsDirty] = useState(false);
  const handlePaymentChange = useCallback((attributes: PurchaseOrderPaymentAttributes): void => {
    setPurchaseOrder((previousPurchaseOrder) => ({ ...previousPurchaseOrder, ...attributes }));
    setIsDirty(true);
  }, []);
  // const [importEntryNumber, setImportEntryNumber] = useState(purchaseOrder.entry_summary_id || '');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    shopify.loading(loading);
  }, [loading]);

  const [emailPdfDialogActive, setEmailPdfDialogActive] = useState(false);
  const [syncCostsModalActive, setSyncCostsModalActive] = useState(false);
  const [syncCostsRequested, setSyncCostsRequested] = useState(false);
  const [closePurchaseOrderModalActive, setClosePurchaseOrderModalActive] = useState(false);
  const [deletionDialogueActive, setDeletionDialogueActive] = useState(false);

  const {
    data: {
      user: { syncbackSetting },
    },
  } = useUser();

  const [errorMessage, setErrorMessage] = useState("");
  const { request, cancel } = useLineItemRequests(setErrorMessage);
  const { visibleColumns, selectedColumns, setSelectedColumns } = useVisibleColumns("purchase-orders-ordered", orderedColumns);
  const visibleLeadingColumns = visibleColumns.filter((column) => column.id !== "received_total");
  const showReceivedTotal = visibleColumns.some((column) => column.id === "received_total");
  const [sortedValue, setSortedValue] = useState(defaultOrderedClosedPurchaseOrderSortValue);
  const handleSortChange = useCallback(([sort]: string[]): void => {
    if (loading) return;
    setSortedValue(sort.replace(" ", "-"));
  }, [loading]);

  const query = useMemo<PurchaseOrderLineItemsQueryParams>(
    () => ({
      by_sort: sortedValue,
    }),
    [sortedValue],
  );

  const automaticSyncback = syncbackSetting === "only_cost" || syncbackSetting === "all_syncback";

  const handleReceiveAll = useCallback((): void => {
    const newQuantity = Object.values(polis).reduce<PurchaseOrderLineItems>(
      (acc: PurchaseOrderLineItems, value: SortablePurchaseOrderLineItem): PurchaseOrderLineItems => {
        // copies the purchase quantity
        const copiedValue = value.quantity || 0;
        const newValue = copiedValue < 0 ? 0 : copiedValue;
        acc[value.id] = {
          ...value,
          quantity: newValue,
          received: newValue,
        } satisfies IPurchaseOrderLineItem;
        return acc;
      },
      {},
    );
    setPolis(newQuantity);
    setIsDirty(true);
  }, [polis, setPolis, setIsDirty]);

  const originalPolis = useRef<PurchaseOrderLineItems>(polis);
  const acceptLineItems = useCallback((response: IPurchaseOrderLineItem[]): void => {
    const purchaseOrderLineItems = makeLineItemsMap<SortablePurchaseOrderLineItem>(response as SortablePurchaseOrderLineItem[]);
    setPolis(purchaseOrderLineItems);
    originalPolis.current = purchaseOrderLineItems;
  }, []);

  const previousQuery = useRef(query);
  useEffect(() => {
    if (previousQuery.current === query) return;
    previousQuery.current = query;
    cancel();
    void request((signal) => getPurchaseOrderHorseVariants(purchaseOrder.id, query, signal), acceptLineItems);
  }, [query, purchaseOrder.id, acceptLineItems, request, cancel]);

  const total = useMemo(() => getTotal(polis), [polis]);
  const {
    costAdjustments,
    setCostAdjustments,
    total: costAdjustmentsTotal,
    attributes: costAdjustmentsAttributes,
    accept: acceptCostAdjustments,
    discard: discardCostAdjustments,
  } = useEditableCostAdjustments(initialResponse.cost_adjustments, total.totalCost);
  const setSummaryIsDirty = useCallback((summaryIsDirty: boolean): void => {
    setIsDirty(isDirty || summaryIsDirty);
  }, [isDirty]);
  
  const acceptResponse = useCallback((response: PurchaseOrderResponse): void => {
    const savedPurchaseOrder = { ...response.purchaseOrder, arrival_date: new Date(response.purchaseOrder.arrival_date) };
    originalPurchaseOrder.current = savedPurchaseOrder;
    setPurchaseOrder(savedPurchaseOrder);
    acceptLineItems(response.line_items);
    acceptCostAdjustments(response.cost_adjustments);
    setIsDirty(false);
  }, [acceptLineItems, acceptCostAdjustments]);

  const handleDiscardAction = useCallback((): void => {
    cancel();
    setIsDirty(false);
    discardCostAdjustments();
    setPurchaseOrder(originalPurchaseOrder.current);
    setPolis(cloneNestedObjects(originalPolis.current));
  }, [discardCostAdjustments, originalPurchaseOrder, setPurchaseOrder, cancel]);

  const payload = useMemo(() => {
    return {
      purchase_order_line_items_attributes: getPurchaseOrderLineItemsPayload(polis),
      cost_adjustments_attributes: costAdjustmentsAttributes,
      note: purchaseOrder.note,
      arrival_date: purchaseOrder.arrival_date,
      label: purchaseOrder.label,
      amount_paid: purchaseOrder.amount_paid,
      payment_due_date: purchaseOrder.payment_due_date,
    };
  }, [
    costAdjustmentsAttributes,
    polis,
    purchaseOrder.note,
    purchaseOrder.arrival_date,
    purchaseOrder.label,
    purchaseOrder.amount_paid,
    purchaseOrder.payment_due_date,
  ]);

  const handleStateChange = useCallback(async (state: PurchaseOrderState, successMessage: string): Promise<void> => {
    cancel();
    setLoading(true);
    const mPayload = {
      purchase_order: {
        sync_costs_requested: syncCostsRequested || automaticSyncback,
        state,
      },
    };
    try {
      const response = await updatePurchaseOrder(purchaseOrder.id, mPayload);
      shopify.toast.show(successMessage);
      onSave(response);
    } catch (err: unknown) {
        Rollbar.error(err, mPayload);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }, [purchaseOrder.id, onSave, cancel, setLoading, setErrorMessage, syncCostsRequested, automaticSyncback]);

  const handleMarkAsReceived = useCallback(async (): Promise<void> => {
    await handleStateChange(PurchaseOrderState.Closed, "Purchase order closed");
  }, [handleStateChange]);

  const handleRevertToDraft = useCallback(async (): Promise<void> => {
    await handleStateChange(PurchaseOrderState.Draft, "Purchase order reverted");
  }, [handleStateChange]);

  const handleSaveOrdered = useCallback(async (): Promise<void> => {
    cancel();
    setLoading(true);
    const mPayload = {
      purchase_order: {
        ...payload,
      },
    };
    try {
      const response = await updatePurchaseOrder(purchaseOrder.id, mPayload, query);
      acceptResponse(response);
      onSave(response);
      shopify.toast.show("Save successful");
    } catch (err: unknown) {
        Rollbar.error(err, mPayload);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }, [acceptResponse, onSave, cancel, purchaseOrder.id, payload, query]);

  const handleLineItemChange = useCallback(
    (mLabel: string, id: number) =>
      (newValue?: string): void => {
        const cleanValue = newValue?.replace(/[^\d.-]/g, "");
        const newLineItems = { ...polis, [id]: { ...polis[id], [mLabel]: cleanValue } };
        setPolis(newLineItems);
        setIsDirty(true);
      },
    [polis, setPolis, setIsDirty],
  );

  const handleNoteChange = useCallback(
    (note: string): void => {
      setPurchaseOrder({ ...purchaseOrder, note });
      setIsDirty(true);
    },
    [purchaseOrder, setPurchaseOrder, setIsDirty],
  );

  const handleDateChange = useCallback((arrival_date: Date): void => {
    setPurchaseOrder({ ...purchaseOrder, arrival_date });
    setIsDirty(true);
  }, [purchaseOrder, setPurchaseOrder, setIsDirty]);

  const handleLabelChange = useCallback(
    (label: string): void => {
      setPurchaseOrder({ ...purchaseOrder, label });
      setIsDirty(true);
    },
    [purchaseOrder, setPurchaseOrder, setIsDirty],
  );

  const handleEmailPdfModalClose = useCallback((): void => {
    setEmailPdfDialogActive(false);
  }, []);

  // const handleImportEntryChange = (value) => {
  //   setImportEntryNumber(value);
  //   setIsDirty(true);
  // };

  const glowRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const incrementLineItem = useCallback((id: number): void => {
    setPolis((prevLineItems: PurchaseOrderLineItems) => {
      const existingLineItem: SortablePurchaseOrderLineItem = prevLineItems[id];
      const existingQuantity = existingLineItem.received || 0;
      const newQuantity = existingQuantity + 1;
      const updatedLineItem: SortablePurchaseOrderLineItem = {
        ...existingLineItem,
        received: newQuantity,
      };

      return {
        ...prevLineItems,
        [id]: updatedLineItem,
      };
    });
    setIsDirty(true);

    // Target the sibling of the input: .Polaris-TextField__Backdrop
    if (glowRef.current) {
      clearTimeout(glowRef.current);
    }
    glowRef.current = addGlowAnimation(`input[name="received"][id="${id}"] ~ .Polaris-TextField__Backdrop`);
  }, []);

  const openDeletionDialogue = useCallback(() => {
    setDeletionDialogueActive(true);
  }, [setDeletionDialogueActive]);

  const purchaseOrderTotal = roundToTwoDecimals(total.totalCost + costAdjustmentsTotal);
  const { paymentStatus, paymentDueDateStatus } = paymentStatuses(
    purchaseOrder.amount_paid,
    purchaseOrder.payment_due_date,
    purchaseOrderTotal,
  );
  const sortedLineItems = useMemo(() => {
    return Object.values(polis).sort(
      (leftLineItem, rightLineItem) => leftLineItem.sortIndex - rightLineItem.sortIndex,
    );
  }, [polis]);

  const navigate = useNavigate();

  const exportPdf = useCallback(async (): Promise<void> => {
    const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
    const filename = `Horse Purchase Order ${purchaseOrderName}.pdf`;
    setLoading(true);
    try {
      await downloadFile(`/purchase_orders/${purchaseOrder.id}.pdf?${window.location.search}`, filename)
    } catch (err: unknown) {
        Rollbar.error(err, { purchaseOrderId: purchaseOrder.id });
        setErrorMessage("Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  }, [purchaseOrder, setLoading, setErrorMessage]);

  const openEmailPdfDialog = useCallback((): void => {
    setEmailPdfDialogActive(true);
  }, [setEmailPdfDialogActive]);

  const exportCsv = useCallback(async (): Promise<void> => {
    const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
    const filename = `Horse Purchase Order ${purchaseOrderName}.csv`;
    await downloadFile(`/purchase_orders/${purchaseOrder.id}.csv${window.location.search}`, filename);
  }, [purchaseOrder]);

  const handleExportDymoCsv = async (): Promise<void> => {
    const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
    const filename = `${purchaseOrderName} Dymo Labels by Horse.csv`;
    await downloadFile(`/purchase_orders/${purchaseOrder.id}/dymo_csv`, filename);
  };

  const openImportCsvModal = useCallback((): void => {
    setImportCSVModalIsOpen(true);
  }, [setImportCSVModalIsOpen]);

  const backAction = useCallback(async (): Promise<void> => {
    await shopify.saveBar.leaveConfirmation();
    navigate("/purchase_orders");
  }, [navigate]);

  const closePurchaseOrder = useCallback(async (): Promise<void> => {
    if (automaticSyncback) {
      await handleMarkAsReceived();
    } else {
      setSyncCostsModalActive(true);
    }
  }, [automaticSyncback, handleMarkAsReceived, setSyncCostsModalActive]);

  const pageMarkup = (
    purchaseOrder && (
      <Page
        actionGroups={[
          {
            title: "More actions",
            actions: [
              {
                content: "Export PDF",
                onAction: exportPdf,
                icon: ExportIcon,
              },
              {
                content: "Email PDF",
                onAction: openEmailPdfDialog,
                icon: EmailFollowUpIcon,
              },
              {
                content: "Export CSV",
                onAction: exportCsv,
                icon: ExportIcon,
              },
              {
                content: "Export Dymo CSV",
                onAction: handleExportDymoCsv,
                icon: ExportIcon,
              },
              {
                content: "Import CSV",
                onAction: openImportCsvModal,
                icon: ImportIcon,
              },
              {
                content: "Revert to draft",
                onAction: handleRevertToDraft,
                disabled: isDirty,
                prefix: <UndoIcon />,
              },
            ],
          },
        ]}
        backAction={{
          content: "Purchase orders",
          onAction: backAction,
        }}
        fullWidth={visibleColumns.length > 0}
        primaryAction={{
          content: "Close purchase order",
          disabled: isDirty,
          onAction: closePurchaseOrder,
        }}
        secondaryActions={[
          {
            content: "Receiving history",
            // url: `/inventory_level_histories?purchase_order_id=${purchaseOrder.id}`,
            plain: true,
            onAction: async (): Promise<void> => {
              await shopify.saveBar.leaveConfirmation();
    
              navigate(`/inventory_level_histories?purchase_order_id=${purchaseOrder.id}`);
            },
          },
        ]}
        title="Receive purchase order"
        titleMetadata={
          <InlineStack gap="200">
            <Badge tone="info">Ordered</Badge>
            <PaymentStatusBadge status={paymentStatus} />
            <PaymentStatusBadge status={paymentDueDateStatus} />
          </InlineStack>
        }
      >
        <SaveBar disabled={loading} handleDiscard={handleDiscardAction} handleSave={handleSaveOrdered} isDirty={isDirty} />

        <EmailPdfDialog
          active={emailPdfDialogActive}
          onClose={handleEmailPdfModalClose}
          onSend={emailPdf}
          purchaseOrder={purchaseOrder}
          setErrorMessage={setErrorMessage}
        />
        <ConfirmationDialog
          active={syncCostsModalActive}
          cancelText="Continue"
          content="Do you want to sync these costs to Shopify? Doing so means overwriting the costs of these variants in your Shopify store(s)."
          okText="Sync costs and continue"
          onCancel={() => {
            setSyncCostsModalActive(false);
            setSyncCostsRequested(false);
            setClosePurchaseOrderModalActive(true);
          }}
          onClose={() => {
            setSyncCostsModalActive(false);
          }}
          onOk={() => {
            setSyncCostsModalActive(false);
            setSyncCostsRequested(true);
            setClosePurchaseOrderModalActive(true);
          }}
          title="Sync costs to Shopify"
        />
        <ConfirmationDialog
          active={closePurchaseOrderModalActive}
          cancelText="No"
          content="Are you sure you want to close this purchase order?"
          okText="Yes"
          onCancel={() => {
            setClosePurchaseOrderModalActive(false);
          }}
          onClose={() => {
            setClosePurchaseOrderModalActive(false);
          }}
          onOk={async () => {
            setClosePurchaseOrderModalActive(false);
            await handleMarkAsReceived();
          }}
          title="Close purchase order"
        />
        <Layout>
          {errorMessage ? (
            <Layout.Section>
              <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
            </Layout.Section>
          ) : null}
          <Layout.Section>
            <LegacyCard>
              <ImportFile<IPurchaseOrderLineItem>
                apiCallMethod={async (formData) => await importPurchaseOrderLineItems(purchaseOrder.id, formData)}
                modalIsOpen={importCSVModalIsOpen}
                setApiResponse={(data: IPurchaseOrderLineItem[]): void => {
                  acceptLineItems(data);
                  onSave({ ...initialResponse, line_items: data });
                }}
                setErrorMessage={setErrorMessage}
                setModalIsOpen={setImportCSVModalIsOpen}
                title="Import purchase order CSV"
              />
              {purchaseOrder.supplier ? (
                <LegacyCard.Section title="Supplier">
                  <Label id="poSupplierName">{purchaseOrder.supplier.name}</Label>
                </LegacyCard.Section>
              ) : (
                <LegacyCard.Section title="Vendor">
                  <Label id="poVendor">
                    <HorseLink url={vendorPath(purchaseOrder.vendor)}>{purchaseOrder.vendor}</HorseLink>
                  </Label>
                </LegacyCard.Section>
              )}
              <LegacyCard.Section title="Destination">
                <Label id="poDestinationName">
                  <HorseLink url={`/horse_locations/${purchaseOrder.horse_location_id}`}>
                    {purchaseOrder.destination}
                  </HorseLink>
                </Label>
              </LegacyCard.Section>
              <LegacyCard.Section>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      autoComplete="off"
                      label="Label"
                      onChange={handleLabelChange}
                      placeholder={purchaseOrder.id.toString()}
                      value={purchaseOrder.label || ""}
                    />
                    <CustomDatePicker
                      date={purchaseOrder.arrival_date}
                      disabled={loading}
                      label="Arrival date"
                      placeholder={today}
                      setDate={handleDateChange}
                    />
                    {/* <TextField
                        onChange={handleImportEntryChange}
                        value={importEntryNumber}
                        label="Customs form number"
                      /> */}
                  </FormLayout.Group>
                </FormLayout>
              </LegacyCard.Section>
            </LegacyCard>
          </Layout.Section>
          <Layout.Section variant="fullWidth">
            <LegacyCard>
              <Box position="relative">
                <Box
                  insetBlockStart="200"
                  insetInlineEnd="2400"
                  position="absolute"
                  zIndex="1"
                >
                  <InlineStack gap="200" wrap={false}>
                    <Scanner incrementLineItem={incrementLineItem} lineItems={polis} />
                    <Button onClick={handleReceiveAll}>Receive all</Button>
                  </InlineStack>
                </Box>
                <TableControls
                  choices={orderedColumns.map((column) => ({ value: column.id, label: column.title }))}
                  disabled={isDirty}
                  onChange={setSelectedColumns}
                  onSort={handleSortChange}
                  selected={selectedColumns}
                  sortOptions={purchaseOrderTableSortOptions}
                  sortSelected={[sortedValue.replace("-", " ")]}
                />
              </Box>
              <IndexTable
                headings={[
                  { title: "Variant" },
                  ...visibleLeadingColumns,
                  // { title: 'Customs form line number' },
                  // { title: 'Description of goods' },
                  // { title: 'Classification number' },
                  // { title: 'Duty rate' },
                  { title: "Quantity ordered" },
                  { title: "Quantity received" },
                  { title: "Cost" },
                  // { title: 'Duties' },
                  { title: "Line total" },
                  ...(showReceivedTotal ? [{ title: "Total received" }] : []),
                ]}
                itemCount={sortedLineItems.length}
                selectable={false}
              >
                <IndexTable.Row id="purchaseOrderOrderedTotalsRow" key={0} position={0} rowType="subheader">
                  <IndexTable.Cell>Totals</IndexTable.Cell>
                  {/* <IndexTable.Cell/>
                      <IndexTable.Cell/>
                      <IndexTable.Cell/>
                      <IndexTable.Cell/> */}
                  {visibleLeadingColumns.map((column) => <IndexTable.Cell key={column.id} />)}
                  <IndexTable.Cell>{commaDelimiter(total.quantity)}</IndexTable.Cell>
                  <IndexTable.Cell>
                    {commaDelimiter(total.received)}
                  </IndexTable.Cell>
                  <IndexTable.Cell>{formatMoney(total.cost, purchaseOrder.currency)}</IndexTable.Cell>
                  {/* <IndexTable.Cell>
                        { formatMoney(total.duties, purchaseOrder.currency) }
                      </IndexTable.Cell> */}
                  <IndexTable.Cell>{formatMoney(total.totalCost, purchaseOrder.currency)}</IndexTable.Cell>
                  {showReceivedTotal ? (
                    <IndexTable.Cell>{formatMoney(total.receivedCost, purchaseOrder.currency)}</IndexTable.Cell>
                  ) : null}
                </IndexTable.Row>
                {sortedLineItems.map((variant: SortablePurchaseOrderLineItem, index) => (
                  <PurchaseOrderOrderedRow
                    handleLineItemChange={handleLineItemChange}
                    index={index + 1}
                    key={`purchase-order-ordered-row:${variant.id}`}
                    purchaseOrder={purchaseOrder}
                    purchaseOrderLineItem={variant}
                    visibleColumns={visibleColumns}
                  />
                ))}
              </IndexTable>
            </LegacyCard>
          </Layout.Section>
          <Layout.Section variant="oneHalf">
            <LegacyCard title="Note to supplier">
              <LegacyCard.Section>
                <TextField
                  autoComplete="off"
                  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                  error={purchaseOrder.note?.length >= 1000 ? "Note is too long" : ""}
                  label="Note"
                  maxLength={1000}
                  multiline={2}
                  onChange={handleNoteChange}
                  value={purchaseOrder.note || ""}
                />
              </LegacyCard.Section>
            </LegacyCard>
          </Layout.Section>
          <Layout.Section variant="oneHalf">
            <LegacyCard>
              <LegacyCard.Section>
                <CostAdjustments
                  costAdjustments={costAdjustments}
                  currency={purchaseOrder.currency}
                  disabled={loading}
                  lineTotal={Number(total.totalCost)}
                  purchaseOrder={purchaseOrder}
                  setCostAdjustments={setCostAdjustments}
                  setIsDirty={setSummaryIsDirty}
                  total={purchaseOrderTotal}
                />
              </LegacyCard.Section>
            </LegacyCard>
            <Payment
              disabled={loading}
              loading={false}
              onChange={handlePaymentChange}
              purchaseOrder={purchaseOrder}
              total={purchaseOrderTotal}
            />
          </Layout.Section>
        </Layout>
        <PageActions
          secondaryActions={[
            {
              content: "Delete",
              destructive: true,
              onAction: openDeletionDialogue,
            },
          ]}
        />
        <DeleteConfirmationDialog
          active={deletionDialogueActive}
          content="Are you sure you want to delete this purchase order? This action cannot be reversed."
          deleteItem={deletePurchaseOrder}
          gotoPath="/purchase_orders"
          itemId={purchaseOrder.id}
          setActive={setDeletionDialogueActive}
          title="Delete purchase"
          toastMessage="Purchase order deleted"
        />
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <Comments />
        </div>
        <Footer pageTitle="purchase orders" url={PURCHASE_ORDER_HELP_PAGE_URL} />
      </Page>
    )
  );
  return <EditorContent disabled={loading}>{pageMarkup}</EditorContent>;

}

export default Index;
